/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import {
  dispositionHasReviewerContent,
  normalizeConfirmedBpm,
  PcgAcousticQuality,
  PcgObservationFinding,
  PcgObservationPattern,
  PcgOtherAcousticObservation,
  PcgRecordingDisposition,
  PcgReviewerObservations,
  PcgReviewRecordingBundle,
  PcgRhythmObservation,
} from "@medaica/common/services/medaica-api-service/pcg-review-types"

/**
 * Reviewer-observations screening vocabulary (handoff v0.8.3.1
 * `recordingFindingLabels` etc., the acoustic rating from Medaica Viewer
 * v0.9.1 `hcpAcousticRatingLabels`): UI label ↔ export enum for the
 * structured observations dialog, the workspace summary line, and the export
 * payload's recording-note labels. The values are pinned server-side
 * (pcg_review schemas.ReviewerObservations) — keep in sync.
 */
const HCP_ACOUSTIC_RATING_LABELS: { [value in PcgAcousticQuality]: string } = {
  clear: "Clear heart sounds",
  weak: "Usable but weak heart sounds",
  not_usable: "Not usable",
}

const OBSERVATION_FINDING_LABELS: { [value in PcgObservationFinding]: string } = {
  murmur_suspected: "Murmur suspected",
  no_murmur_suspected: "No murmur suspected",
  unsure: "Unable to assess",
}

const OBSERVATION_PATTERN_LABELS: { [value in PcgObservationPattern]: string } = {
  systolic_ejection: "Systolic ejection",
  holosystolic: "Holosystolic",
  decrescendo_systolic: "Decrescendo systolic",
  early_diastolic: "Early diastolic",
  mid_diastolic: "Mid-diastolic",
  late_diastolic: "Late diastolic",
  continuous: "Continuous",
  timing_indeterminate: "Timing indeterminate",
}

/** The dialog's pattern column layout (handoff fieldset legends). */
const OBSERVATION_PATTERN_GROUPS: { legend: string; options: PcgObservationPattern[] }[] = [
  { legend: "Systolic", options: ["systolic_ejection", "holosystolic", "decrescendo_systolic"] },
  { legend: "Diastolic", options: ["early_diastolic", "mid_diastolic", "late_diastolic"] },
  { legend: "Other", options: ["continuous", "timing_indeterminate"] },
]

const OTHER_ACOUSTIC_OBSERVATION_LABELS: { [value in PcgOtherAcousticObservation]: string } = {
  click_suspected: "Click suspected",
  rub_like_sound: "Rub-like sound suspected",
  artifact_or_non_cardiac: "Non-cardiac sound / artifact",
}

const RHYTHM_OBSERVATION_LABELS: { [value in PcgRhythmObservation]: string } = {
  irregular_rhythm: "Irregular rhythm suspected",
  rate_unreliable: "Rate estimate unreliable",
  extra_heart_sound: "Extra heart sound suspected",
}

const EMPTY_OBSERVATIONS: PcgReviewerObservations = {
  acousticQuality: null,
  confirmedBpm: null,
  finding: null,
  pattern: null,
  otherObservations: [],
  rhythmObservations: [],
}

/**
 * One-line summary of a disposition's reviewer observations (handoff
 * `recordingNoteSummary`): the acoustic rating and confirmed BPM first, then
 * the selected labels, then the internal note, then "Report note added" when
 * there is a Report note (its wording is not repeated), " — "-joined.
 */
const reviewerObservationSummary = (disposition: PcgRecordingDisposition | null): string => {
  if (!disposition) {
    return ""
  }
  const observations = disposition.observations
  const confirmedBpm = normalizeConfirmedBpm(observations?.confirmedBpm)
  return [
    observations?.acousticQuality ? HCP_ACOUSTIC_RATING_LABELS[observations.acousticQuality] : "",
    confirmedBpm !== null ? `Confirmed BPM ${confirmedBpm}` : "",
    observations?.finding ? OBSERVATION_FINDING_LABELS[observations.finding] : "",
    observations?.pattern ? OBSERVATION_PATTERN_LABELS[observations.pattern] : "",
    ...(observations?.otherObservations ?? []).map((value) => OTHER_ACOUSTIC_OBSERVATION_LABELS[value]),
    ...(observations?.rhythmObservations ?? []).map((value) => RHYTHM_OBSERVATION_LABELS[value]),
    (disposition.note ?? "").trim(),
    (observations?.reportNote ?? "").trim() ? "Report note added" : "",
  ]
    .filter(Boolean)
    .join(" — ")
}

/**
 * The handoff viewer's exam-wide notes line under the review buttons: every
 * recording that carries reviewer observations, in exam order, as "Aortic:
 * Murmur suspected — loudest at the apex · Pulmonic: …"; null when none does.
 */
const examNotesSummary = (
  recordings: { label: string; disposition: PcgRecordingDisposition | null }[]
): string | null => {
  const rows = recordings
    .map((recording, index) => {
      const text = reviewerObservationSummary(recording.disposition)
      const label = recording.label || `Recording ${index + 1}`
      return text ? `${label}: ${text}` : ""
    })
    .filter(Boolean)
  return rows.length ? rows.join(" · ") : null
}

/**
 * One `medaica.pcg.recording-note.v4` export row (handoff v0.8.3.1
 * `window.__medaicaExamRecordingNotes` entry, with the Medaica Viewer v0.9.1
 * acoustic rating, confirmed BPM and — since pcg-viewer-mvp git 20ac6e4 —
 * Report note): the structured selections and internal free text of a
 * recording's reviewer observations, with display labels resolved so the
 * Medaica Report needs no vocabulary of its own, marked internal review
 * context; the Report note is the only part meant for the patient and care
 * team. The `characterization`/`characterizationLabel` pair duplicates the
 * pattern for consumers of the v2 field name (handoff compatibility).
 */
type PcgRecordingNoteExport = {
  schema: "medaica.pcg.recording-note.v4"
  examId: string
  recordingId: string
  recordingSequence: number
  position: string
  acousticFinding: PcgObservationFinding | null
  acousticFindingLabel: string | null
  dominantPattern: PcgObservationPattern | null
  dominantPatternLabel: string | null
  otherAcousticObservations: PcgOtherAcousticObservation[]
  otherAcousticObservationLabels: string[]
  rhythmObservations: PcgRhythmObservation[]
  rhythmObservationLabels: string[]
  characterization: PcgObservationPattern | null
  characterizationLabel: string | null
  text: string
  /** Report-facing wording ("" when none). */
  reportNote: string
  source: string
  visibility: "internal_review"
  patientAccessExpected: false
  /** "patient_and_care_team" when there is a Report note, else null. */
  reportNoteVisibility: "patient_and_care_team" | null
  reviewStatus: "recorded"
  acousticQuality: PcgAcousticQuality | null
  acousticQualityLabel: string | null
  /** The reviewer's confirmed heart rate for this recording (whole number, 25–220), or null. */
  hcpConfirmedBpm: number | null
}

/**
 * The exam's reviewer observations as export recording-note rows, in exam
 * capture order — the whole list rides in every evidence package's
 * examContext (the handoff's exam-wide `__medaicaExamRecordingNotes`).
 * Recordings without reviewer content contribute no row.
 */
const buildRecordingNoteExports = (
  examId: string,
  recordings: PcgReviewRecordingBundle[]
): PcgRecordingNoteExport[] => {
  return recordings
    .map((recording, index): PcgRecordingNoteExport | null => {
      const disposition = recording.disposition
      if (!disposition || !dispositionHasReviewerContent(disposition)) {
        return null
      }
      const observations = disposition.observations
      const acousticQuality = observations?.acousticQuality ?? null
      const finding = observations?.finding ?? null
      const pattern = observations?.pattern ?? null
      const otherObservations = observations?.otherObservations ?? []
      const rhythmObservations = observations?.rhythmObservations ?? []
      const reportNote = (observations?.reportNote ?? "").trim()
      return {
        schema: "medaica.pcg.recording-note.v4",
        examId,
        recordingId: recording.auscultationId,
        recordingSequence: index + 1,
        position: recording.site?.label ?? recording.auscultationPoint ?? `Recording ${index + 1}`,
        acousticFinding: finding,
        acousticFindingLabel: finding ? OBSERVATION_FINDING_LABELS[finding] : null,
        dominantPattern: pattern,
        dominantPatternLabel: pattern ? OBSERVATION_PATTERN_LABELS[pattern] : null,
        otherAcousticObservations: otherObservations,
        otherAcousticObservationLabels: otherObservations.map((value) => OTHER_ACOUSTIC_OBSERVATION_LABELS[value]),
        rhythmObservations,
        rhythmObservationLabels: rhythmObservations.map((value) => RHYTHM_OBSERVATION_LABELS[value]),
        characterization: pattern,
        characterizationLabel: pattern ? OBSERVATION_PATTERN_LABELS[pattern] : null,
        text: (disposition.note ?? "").trim(),
        reportNote,
        source: "Medaica Viewer",
        visibility: "internal_review",
        patientAccessExpected: false,
        reportNoteVisibility: reportNote ? "patient_and_care_team" : null,
        reviewStatus: "recorded",
        acousticQuality,
        acousticQualityLabel: acousticQuality ? HCP_ACOUSTIC_RATING_LABELS[acousticQuality] : null,
        hcpConfirmedBpm: normalizeConfirmedBpm(observations?.confirmedBpm),
      }
    })
    .filter((note): note is PcgRecordingNoteExport => note !== null)
}

export {
  HCP_ACOUSTIC_RATING_LABELS,
  OBSERVATION_FINDING_LABELS,
  OBSERVATION_PATTERN_LABELS,
  OBSERVATION_PATTERN_GROUPS,
  OTHER_ACOUSTIC_OBSERVATION_LABELS,
  RHYTHM_OBSERVATION_LABELS,
  EMPTY_OBSERVATIONS,
  reviewerObservationSummary,
  examNotesSummary,
  buildRecordingNoteExports,
}
export type { PcgRecordingNoteExport }
